import { Languages, Sparkles } from "lucide-react";
import { cn } from "../lib/cn";

export type TranslationBubbleProps = {
  role: "self" | "other";
  original: string;
  translated: string;
  sourceLang: "ko" | "ja";
  targetLang: "ko" | "ja";
  confidence?: number; // 0..1
  formality?: "polite" | "casual";
  timestamp?: string;
  className?: string;
};

export function TranslationBubble({
  role,
  original,
  translated,
  sourceLang,
  targetLang,
  confidence = 0.9,
  formality = "polite",
  timestamp,
  className,
}: TranslationBubbleProps) {
  const isSelf = role === "self";
  const confidencePct = Math.round(confidence * 100);
  const confidenceTone =
    confidence >= 0.85
      ? "text-success-500"
      : confidence >= 0.65
        ? "text-warning-500"
        : "text-danger-500";

  return (
    <div
      className={cn(
        "flex w-full",
        isSelf ? "justify-end" : "justify-start",
        className,
      )}
    >
      <div
        className={cn(
          "max-w-[82%] sm:max-w-[70%] lg:max-w-[55%] rounded-2xl px-4 py-3",
          isSelf
            ? "bg-gradient-to-br from-primary-500/80 to-primary-700/70 text-white shadow-glass-md"
            : "kj-glass",
        )}
      >
        <p className="text-base leading-relaxed">{translated}</p>
        <details className="mt-1.5 text-xs opacity-80">
          <summary className="cursor-pointer select-none list-none flex items-center gap-1">
            <Languages className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="uppercase tracking-wide">
              {sourceLang} → {targetLang}
            </span>
            <span className={cn("ml-2", confidenceTone)}>
              {confidencePct}%
            </span>
          </summary>
          <p className="mt-1 italic opacity-90">{original}</p>
        </details>
        <div className="mt-1 flex items-center justify-between text-[0.7rem] opacity-60">
          <span className="inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            {formality === "polite" ? "존댓말" : "반말"}
          </span>
          {timestamp && <span>{timestamp}</span>}
        </div>
      </div>
    </div>
  );
}
